import * as fs from 'fs-extra';
import * as path from 'path';
import { spawn, ChildProcess } from 'child_process';
import { 
  Migration, 
  MigrationResult, 
  MigrationType,
  ProjectStructure, 
  FileChange, 
  ValidationResult 
} from './types';

export interface ClaudeMigrationOptions {
  claudeCommand?: string; // Path to claude-code CLI
  maxTokens?: number;
  model?: string;
  timeout?: number; // milliseconds
  dryRun?: boolean;
}

export interface ClaudeMigrationInstruction {
  id: string;
  version: string;
  title: string;
  description: string;
  prompt: string;
  filePatterns: string[];
  dependencies?: {
    add: string[] | Array<{ name: string; version: string; dev?: boolean }>;
    remove: string[];
    update: Array<{ name: string; from: string; to: string }>;
  };
  postProcessing?: {
    commands: string[];
    lintFix?: boolean;
    formatCode?: boolean;
  };
  validationRules?: ClaudeValidationRule[];
}

export interface ClaudeValidationRule {
  name: string;
  pattern: RegExp;
  message: string;
  severity: 'error' | 'warning';
}

export interface ClaudeExecutionContext {
  projectPath: string;
  tempWorkspace: string;
  instruction: ClaudeMigrationInstruction;
  options: ClaudeMigrationOptions;
}

/**
 * Claude Code-powered migration engine for intelligent code transformations
 */
export class ClaudeMigrationEngine {
  private options: ClaudeMigrationOptions;

  constructor(options: ClaudeMigrationOptions = {}) {
    this.options = {
      claudeCommand: options.claudeCommand || this.detectClaudeCommand(),
      maxTokens: options.maxTokens || 8192,
      model: options.model || 'claude-3-5-sonnet-20241022',
      timeout: options.timeout || 300000, // 5 minutes
      dryRun: options.dryRun || false,
      ...options
    };
  }

  /**
   * Detect available Claude command
   */
  private detectClaudeCommand(): string {
    const { execSync } = require('child_process');
    
    // Try claude-code first (preferred)
    try {
      execSync('which claude-code', { stdio: 'pipe' });
      return 'claude-code';
    } catch (error) {
      // claude-code not found
    }
    
    // Fallback to claude
    try {
      execSync('which claude', { stdio: 'pipe' });
      return 'claude';
    } catch (error) {
      // claude not found either
    }
    
    // No Claude command available
    return '';
  }

  /**
   * Create a Claude-powered migration from an instruction
   */
  createMigration(instruction: ClaudeMigrationInstruction): Migration {
    return {
      id: instruction.id,
      version: instruction.version,
      type: MigrationType.TRANSFORMATION,
      description: instruction.title,
      breaking: (instruction.dependencies?.remove?.length ?? 0) > 0,
      transform: async (project: ProjectStructure) => 
        this.executeClaudeMigration(project, instruction),
      validate: instruction.validationRules ? 
        async (project: ProjectStructure) => this.validateMigration(project, instruction) : 
        undefined,
      rollback: async (project: ProjectStructure, backup) => {
        // Restore files from backup
        for (const file of backup.files) {
          const filePath = path.join(project.path, file.path);
          await fs.writeFile(filePath, file.content, 'utf8');
        }
      },
      estimateTime: () => 120000 // 2 minutes default for Claude operations
    };
  }

  /**
   * Execute a Claude-powered migration
   */
  private async executeClaudeMigration(
    project: ProjectStructure, 
    instruction: ClaudeMigrationInstruction
  ): Promise<MigrationResult> {
    const tempWorkspace = await this.createTempWorkspace(project, instruction);
    const context: ClaudeExecutionContext = {
      projectPath: project.path,
      tempWorkspace,
      instruction,
      options: this.options
    };

    try {
      // Step 1: Prepare Claude prompt with project context
      const prompt = await this.buildClaudePrompt(project, instruction);
      
      // Step 2: Execute Claude Code
      const claudeResult = await this.executeClaude(context, prompt);
      
      // Step 3: Parse and validate results
      const changes = await this.parseClaudeChanges(context, claudeResult);
      
      // Step 4: Apply post-processing if configured
      if (instruction.postProcessing) {
        await this.applyPostProcessing(context, changes);
      }

      // Step 5: Copy changes back to project
      if (!this.options.dryRun) {
        await this.applyChanges(project.path, changes);
      }

      return {
        success: true,
        changes: changes,
        warnings: [`Applied ${changes.length} file changes using Claude Code`]
      };
    } catch (error) {
      return {
        success: false,
        changes: [],
        errors: [error instanceof Error ? error.message : String(error)]
      };
    } finally {
      // Cleanup temp workspace
      await fs.remove(tempWorkspace);
    }
  }

  /**
   * Create temporary workspace with relevant project files
   */
  private async createTempWorkspace(
    project: ProjectStructure, 
    instruction: ClaudeMigrationInstruction
  ): Promise<string> {
    const tempDir = path.join(process.cwd(), '.dezzy-migration-temp', `migration-${Date.now()}`);
    await fs.ensureDir(tempDir);

    // Copy relevant files based on patterns
    for (const pattern of instruction.filePatterns) {
      const matchingFiles = project.files.filter(file => 
        file.path.match(new RegExp(pattern))
      );

      for (const file of matchingFiles) {
        const targetPath = path.join(tempDir, file.path);
        await fs.ensureDir(path.dirname(targetPath));
        await fs.writeFile(targetPath, file.content, 'utf8');
      }
    }

    // Copy package.json for dependency context
    if (project.packageJson) {
      await fs.writeJson(path.join(tempDir, 'package.json'), project.packageJson, { spaces: 2 });
    }

    // Copy Dezzy config if exists
    if (project.dezzyConfig) {
      await fs.writeJson(path.join(tempDir, 'dezzy.config.json'), project.dezzyConfig, { spaces: 2 });
    }

    return tempDir;
  }

  /**
   * Build Claude prompt with project context and migration instructions
   */
  private async buildClaudePrompt(
    project: ProjectStructure, 
    instruction: ClaudeMigrationInstruction
  ): Promise<string> {
    const filesSummary = instruction.filePatterns.map(pattern => {
      const matchingFiles = project.files.filter(file => 
        file.path.match(new RegExp(pattern))
      );
      return `${pattern}: ${matchingFiles.length} files`;
    }).join('\n');

    return `
# Dezzy Migration: ${instruction.title}

## Project Context
- **Project Path**: ${project.path}
- **Current Version**: ${project.version}
- **Target Version**: ${instruction.version}
- **Relevant Files**: 
${filesSummary}

## Migration Objective
${instruction.description}

## Dependencies to Update
${instruction.dependencies ? `
- Add: ${instruction.dependencies.add.join(', ') || 'none'}
- Remove: ${instruction.dependencies.remove.join(', ') || 'none'}  
- Update: ${instruction.dependencies.update.map(u => `${u.name} (${u.from} → ${u.to})`).join(', ') || 'none'}
` : 'No dependency changes'}

## Instructions
${instruction.prompt}

## Requirements
1. Maintain existing functionality
2. Follow best practices and patterns
3. Preserve code comments and documentation
4. Use TypeScript when possible
5. Ensure all imports are correctly updated
6. Test critical changes if test files exist

Please execute the migration systematically across all relevant files in the workspace.
`.trim();
  }

  /**
   * Check if Claude is available and prompt user if needed
   */
  private async ensureClaudeAvailable(instruction: ClaudeMigrationInstruction): Promise<void> {
    if (this.options.claudeCommand) {
      return; // Claude is available
    }

    // If this is a dry run, just skip Claude entirely
    if (this.options.dryRun) {
      throw new Error('Claude CLI not available. Migration skipped (dry run mode).');
    }

    // Environment variable for CI/testing - skip Claude without prompting
    if (process.env.DEZZY_SKIP_CLAUDE === 'true' || process.env.CI === 'true') {
      throw new Error('Claude CLI not available. Migration skipped (CI/testing mode).');
    }

    // Import inquirer dynamically for ES module compatibility
    const inquirerModule = await import('inquirer');
    const inquirer = inquirerModule.default || inquirerModule;

    console.log('🤖 Claude-Powered Migration Available');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log(`📋 Migration: ${instruction.title}`);
    console.log(`🎯 Purpose: ${instruction.description}`);
    console.log('');
    console.log('🔧 What this migration will do:');
    
    if (instruction.dependencies?.add?.length) {
      const addDeps = instruction.dependencies.add.map(d => 
        typeof d === 'string' ? d : d.name
      ).join(', ');
      console.log(`   • Add dependencies: ${addDeps}`);
    }
    if (instruction.dependencies?.remove?.length) {
      console.log(`   • Remove dependencies: ${instruction.dependencies.remove.join(', ')}`);
    }
    
    console.log(`   • Transform files matching: ${instruction.filePatterns.join(', ')}`);
    console.log('   • Apply intelligent code transformations using AI');
    console.log('   • Maintain existing functionality and patterns');
    
    if (instruction.postProcessing?.lintFix) {
      console.log('   • Run linting and formatting');
    }
    
    console.log('');
    console.log('⚠️  Claude CLI not found on your system');
    console.log('');
    console.log('To use this intelligent migration, you need Claude CLI installed:');
    console.log('   • Claude Code CLI: https://claude.ai/code (preferred)');
    console.log('   • Regular Claude CLI: https://claude.ai/cli');
    console.log('');

    const { shouldProceed } = await inquirer.prompt([{
      type: 'confirm',
      name: 'shouldProceed',
      message: 'Would you like to install Claude CLI and run this migration manually?',
      default: false
    }]);

    if (shouldProceed) {
      console.log('');
      console.log('🚀 Manual Migration Instructions:');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('1. Install Claude CLI:');
      console.log('   npm install -g @anthropic-ai/claude-cli');
      console.log('');
      console.log('2. Run the migration again:');
      console.log('   dezzy migrate');
      console.log('');
      console.log('Or you can apply the changes manually:');
      console.log('');
      console.log(this.generateManualMigrationInstructions(instruction));
    }

    throw new Error('Claude CLI not available. Migration skipped.');
  }

  /**
   * Generate manual migration instructions for the user
   */
  private generateManualMigrationInstructions(instruction: ClaudeMigrationInstruction): string {
    let instructions = '';
    
    if (instruction.dependencies?.add?.length || instruction.dependencies?.remove?.length) {
      instructions += '📦 Dependencies:\n';
      if (instruction.dependencies.add?.length) {
        const addCommands = instruction.dependencies.add.map(d => {
          if (typeof d === 'string') {
            return d;
          } else {
            return `${d.name}@${d.version}`;
          }
        }).join(' ');
        instructions += `   Add: npm install ${addCommands}\n`;
      }
      if (instruction.dependencies.remove?.length) {
        instructions += `   Remove: npm uninstall ${instruction.dependencies.remove.join(' ')}\n`;
      }
      instructions += '\n';
    }

    instructions += '🔄 File Transformations:\n';
    instructions += `   Files to modify: ${instruction.filePatterns.join(', ')}\n`;
    instructions += '\n';
    
    // Include key parts of the prompt as manual instructions
    if (instruction.prompt.includes('Tailwind')) {
      instructions += '🎨 Tailwind Migration Steps:\n';
      instructions += '   1. Install Tailwind CSS and configure tailwind.config.js\n';
      instructions += '   2. Replace CSS modules with Tailwind utility classes\n';
      instructions += '   3. Convert design tokens to Tailwind theme values\n';
      instructions += '   4. Update component styling from CSS-in-JS to Tailwind\n';
      instructions += '   5. Remove old styled-components dependencies\n';
    }

    return instructions;
  }

  /**
   * Execute Claude Code command
   */
  private async executeClaude(
    context: ClaudeExecutionContext, 
    prompt: string
  ): Promise<string> {
    // Ensure Claude is available (this will prompt user if needed)
    await this.ensureClaudeAvailable(context.instruction);
    
    // If we get here, Claude is definitely available
    if (!this.options.claudeCommand) {
      throw new Error('Internal error: Claude command should be available at this point');
    }

    return new Promise((resolve, reject) => {
      const args = this.buildClaudeArgs(prompt);

      const claude = spawn(this.options.claudeCommand!, args.filter((arg): arg is string => arg !== undefined), {
        cwd: context.tempWorkspace,
        stdio: ['pipe', 'pipe', 'pipe']
      }) as ChildProcess;

      let stdout = '';
      let stderr = '';

      claude.stdout?.on('data', (data: Buffer) => {
        stdout += data.toString();
      });

      claude.stderr?.on('data', (data: Buffer) => {
        stderr += data.toString();
      });

      const timeout = setTimeout(() => {
        claude.kill();
        reject(new Error(`Claude execution timed out after ${this.options.timeout}ms`));
      }, this.options.timeout);

      claude.on('close', (code: number | null) => {
        clearTimeout(timeout);
        if (code === 0) {
          resolve(stdout);
        } else {
          reject(new Error(`Claude execution failed with code ${code}: ${stderr}`));
        }
      });

      claude.on('error', (error: Error) => {
        clearTimeout(timeout);
        reject(new Error(`Failed to start Claude: ${error.message}`));
      });
    });
  }

  /**
   * Build Claude command arguments based on the detected command
   */
  private buildClaudeArgs(prompt: string): string[] {
    if (this.options.claudeCommand === 'claude-code') {
      // Claude Code CLI arguments
      return [
        '--model', this.options.model || 'claude-3-5-sonnet-20241022',
        '--max-tokens', (this.options.maxTokens || 8192).toString(),
        '--message', prompt
      ];
    } else {
      // Regular Claude CLI arguments (different format)
      return [
        'chat',
        '--model', this.options.model || 'claude-3-5-sonnet-20241022',
        '--', // End of options
        prompt
      ];
    }
  }

  /**
   * Parse Claude's changes and detect file modifications
   */
  private async parseClaudeChanges(
    context: ClaudeExecutionContext, 
    claudeOutput: string
  ): Promise<FileChange[]> {
    const changes: FileChange[] = [];
    const tempWorkspace = context.tempWorkspace;

    // Get all files in temp workspace after Claude execution
    const allFiles = await this.getAllFiles(tempWorkspace);

    for (const filePath of allFiles) {
      const relativePath = path.relative(tempWorkspace, filePath);
      const newContent = await fs.readFile(filePath, 'utf8');

      // Check if this file was in the original project
      const originalFile = context.instruction.filePatterns.some(pattern =>
        relativePath.match(new RegExp(pattern))
      );

      if (originalFile) {
        // Find corresponding original content
        const originalPath = path.join(context.projectPath, relativePath);
        try {
          const oldContent = await fs.readFile(originalPath, 'utf8');
          if (oldContent !== newContent) {
            changes.push({
              path: relativePath,
              type: 'update',
              oldContent,
              newContent
            });
          }
        } catch (error) {
          // File doesn't exist in original, so it's new
          changes.push({
            path: relativePath,
            type: 'create',
            newContent
          });
        }
      } else {
        // New file created by Claude
        changes.push({
          path: relativePath,
          type: 'create',
          newContent
        });
      }
    }

    return changes;
  }

  /**
   * Apply post-processing steps
   */
  private async applyPostProcessing(
    context: ClaudeExecutionContext,
    changes: FileChange[]
  ): Promise<void> {
    const { postProcessing } = context.instruction;
    if (!postProcessing) return;

    // Run custom commands
    for (const command of postProcessing.commands || []) {
      await this.runCommand(command, context.tempWorkspace);
    }

    // Run linting
    if (postProcessing.lintFix) {
      try {
        await this.runCommand('npm run lint -- --fix', context.tempWorkspace);
      } catch (error) {
        console.warn('Lint fix failed:', error);
      }
    }

    // Format code
    if (postProcessing.formatCode) {
      try {
        await this.runCommand('npx prettier --write "**/*.{ts,tsx,js,jsx,json,css,md}"', context.tempWorkspace);
      } catch (error) {
        console.warn('Code formatting failed:', error);
      }
    }
  }

  /**
   * Apply changes to the actual project
   */
  private async applyChanges(projectPath: string, changes: FileChange[]): Promise<void> {
    for (const change of changes) {
      const targetPath = path.join(projectPath, change.path);
      
      switch (change.type) {
        case 'create':
        case 'update':
          if (change.newContent) {
            await fs.ensureDir(path.dirname(targetPath));
            await fs.writeFile(targetPath, change.newContent, 'utf8');
          }
          break;
        case 'delete':
          await fs.remove(targetPath);
          break;
        case 'move':
          if (change.oldPath) {
            const oldPath = path.join(projectPath, change.oldPath);
            await fs.move(oldPath, targetPath);
          }
          break;
      }
    }
  }

  /**
   * Validate migration results
   */
  private async validateMigration(
    project: ProjectStructure,
    instruction: ClaudeMigrationInstruction
  ): Promise<ValidationResult> {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!instruction.validationRules) {
      return { valid: true };
    }

    for (const file of project.files) {
      for (const rule of instruction.validationRules) {
        if (rule.pattern.test(file.content)) {
          if (rule.severity === 'error') {
            errors.push(`${file.path}: ${rule.message}`);
          } else {
            warnings.push(`${file.path}: ${rule.message}`);
          }
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
      warnings: warnings.length > 0 ? warnings : undefined
    };
  }

  /**
   * Helper methods
   */
  private async getAllFiles(dir: string): Promise<string[]> {
    const files: string[] = [];
    const items = await fs.readdir(dir);
    
    for (const item of items) {
      const fullPath = path.join(dir, item);
      const stat = await fs.stat(fullPath);
      
      if (stat.isDirectory()) {
        files.push(...await this.getAllFiles(fullPath));
      } else {
        files.push(fullPath);
      }
    }
    
    return files;
  }

  private async runCommand(command: string, cwd: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const child = spawn('sh', ['-c', command], { cwd, stdio: 'pipe' });
      let stdout = '';
      let stderr = '';

      child.stdout.on('data', (data) => stdout += data.toString());
      child.stderr.on('data', (data) => stderr += data.toString());

      child.on('close', (code) => {
        if (code === 0) {
          resolve(stdout);
        } else {
          reject(new Error(`Command failed: ${stderr}`));
        }
      });
    });
  }
}